import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import supabase from "../../supabaseClient";
import api from "../../config/api";

const ACTIVE_STATUSES = [
  "in warehouse",
  "departure at origin",
  "in transit",
  "arrival at destination",
  "notification",
  "custom clearance",
];

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-PH", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const normalize = (value) => String(value || "").trim().toLowerCase();

const getStatusToneClass = (value) => {
  const status = normalize(value);
  if (["approved", "checked", "completed", "verified"].includes(status)) {
    return "bg-emerald-50 text-emerald-700 ring-emerald-100";
  }
  if (["pending", "partially_approved", "submitted", "draft"].includes(status)) {
    return "bg-amber-50 text-amber-700 ring-amber-100";
  }
  if (["cancelled", "returned", "rejected"].includes(status)) {
    return "bg-rose-50 text-rose-700 ring-rose-100";
  }
  return "bg-slate-100 text-slate-700 ring-slate-200";
};

const getRequestCoverageClass = (hasRequest) =>
  hasRequest
    ? "bg-emerald-50 text-emerald-700 ring-emerald-100"
    : "bg-rose-50 text-rose-700 ring-rose-100";

const isActiveShipment = (shipment) => {
  const status = normalize(shipment?.status);
  const isAirExportDoorOrPortToPortAtArrival =
    shipment?.shipment_type === "AIR" &&
    (shipment?.shipment_mode === "EXPORT" ||
      shipment?.shipment_mode === "EXPORT-EVENT") &&
    (shipment?.delivery_mode === "DOOR-PORT" ||
      shipment?.delivery_mode === "PORT-PORT") &&
    status === "arrival at destination";

  return !isAirExportDoorOrPortToPortAtArrival && ACTIVE_STATUSES.includes(status);
};

const isNominationShipment = (shipment) => normalize(shipment?.status) === "order placed";

const buildJoRows = (shipments, requests) => {
  const requestMap = new Map();
  requests.forEach((request) => {
    const key = normalize(request?.job_order);
    if (!key) return;
    if (!requestMap.has(key)) {
      requestMap.set(key, []);
    }
    requestMap.get(key).push(request);
  });

  const jobOrderMap = new Map();

  shipments.forEach((shipment) => {
    const jobOrder = String(shipment?.job_order || "").trim();
    const key = normalize(jobOrder);
    if (!key) return;

    const stage = isNominationShipment(shipment)
      ? "Nomination"
      : isActiveShipment(shipment)
        ? "Active"
        : null;

    if (!stage) return;

    const shipmentTimestamp = new Date(shipment?.date || 0).getTime();

    const existing = jobOrderMap.get(key);
    const shouldReplace =
      !existing ||
      (existing.stage !== "Active" && stage === "Active") ||
      shipmentTimestamp > existing.shipmentTimestamp;

    if (!shouldReplace) return;

    const linkedRequests = [...(requestMap.get(key) || [])].sort((a, b) => {
      const aTime = new Date(a?.updated_at || a?.created_at || 0).getTime();
      const bTime = new Date(b?.updated_at || b?.created_at || 0).getTime();
      return bTime - aTime;
    });

    const latestRequest = linkedRequests[0] || null;
    const totalRequestAmount = linkedRequests.reduce(
      (sum, request) => sum + Number(request?.total_amount || 0),
      0
    );

    jobOrderMap.set(key, {
      id: key,
      job_order: jobOrder,
      tracking_no: shipment?.tracking_no || "—",
      corporate_client: shipment?.corporate_client || "—",
      stage,
      shipment_status: shipment?.status || "—",
      shipment_type: shipment?.shipment_type || "—",
      shipment_mode: shipment?.shipment_mode || "—",
      assigned_to:
        shipment?.current_user_name ||
        shipment?.receiver_name ||
        shipment?.shipper_name ||
        "—",
      shipment_date: shipment?.date || null,
      shipmentTimestamp,
      linked_requests: linkedRequests.map((request) => ({
        id: request?.id,
        rfp_no: request?.rfp_no || "—",
        payee: request?.payee || "—",
        status: request?.status || null,
      })),
      request_count: linkedRequests.length,
      has_request: linkedRequests.length > 0,
      latest_rfp_no: latestRequest?.rfp_no || "—",
      latest_payee: latestRequest?.payee || "—",
      latest_request_status: latestRequest?.status || null,
      total_request_amount: totalRequestAmount,
    });
  });

  return Array.from(jobOrderMap.values()).sort(
    (a, b) => b.shipmentTimestamp - a.shipmentTimestamp
  );
};

const JoRequests = ({
  portalLabel = "Accounting Portal",
  pageTitle = "JO Requests",
  pageDescription = "Track all active and nomination job orders and see whether a request has already been submitted for each JO.",
}) => {
  const user = useSelector((state) => state.auth.user);
  const [shipments, setShipments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [coverageFilter, setCoverageFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");

      try {
        let shipmentQuery = supabase
          .from("shipments")
          .select(
            "shipment_id, tracking_no, job_order, status, date, corporate_client, shipment_type, shipment_mode, delivery_mode, current_user_name, receiver_name, shipper_name"
          );

        if (String(user?.role || "").toLowerCase() === "corporate" && user?.department) {
          shipmentQuery = shipmentQuery.eq("corporate_client", user.department);
        }

        const [{ data: shipmentRows, error: shipmentError }, requestsResponse] =
          await Promise.all([
            shipmentQuery,
            api.get("/api/accounting/requests"),
          ]);

        if (shipmentError) {
          throw shipmentError;
        }

        setShipments(shipmentRows || []);
        setRequests(requestsResponse.data?.data || []);
      } catch (loadError) {
        console.error("Failed to load JO Requests:", loadError);
        setError(loadError?.message || "Failed to load JO Requests");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user?.department, user?.role]);

  const joRows = useMemo(() => buildJoRows(shipments, requests), [shipments, requests]);

  const filteredRows = useMemo(() => {
    const term = normalize(searchTerm);

    return joRows.filter((row) => {
      const matchesSearch =
        !term ||
        [
          row.job_order,
          row.tracking_no,
          row.corporate_client,
          row.assigned_to,
          row.latest_rfp_no,
          row.latest_payee,
          row.shipment_status,
          row.latest_request_status,
          ...(row.linked_requests || []).flatMap((request) => [
            request.rfp_no,
            request.payee,
            request.status,
          ]),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(term);

      const matchesStage = stageFilter === "all" || normalize(row.stage) === stageFilter;
      const matchesCoverage =
        coverageFilter === "all" ||
        (coverageFilter === "with-request" && row.has_request) ||
        (coverageFilter === "without-request" && !row.has_request);

      return matchesSearch && matchesStage && matchesCoverage;
    });
  }, [coverageFilter, joRows, searchTerm, stageFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [coverageFilter, searchTerm, stageFilter]);

  const summary = useMemo(() => {
    const total = joRows.length;
    const active = joRows.filter((row) => row.stage === "Active").length;
    const nomination = joRows.filter((row) => row.stage === "Nomination").length;
    const withRequest = joRows.filter((row) => row.has_request).length;
    const withoutRequest = joRows.filter((row) => !row.has_request).length;

    return { total, active, nomination, withRequest, withoutRequest };
  }, [joRows]);

  const pageSize = 12;
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="space-y-5 font-poppins">
      <header className="rounded-3xl border border-slate-200 bg-white/95 p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-4xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
              {portalLabel}
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-blue">
              {pageTitle}
            </h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">{pageDescription}</p>
          </div>

          <div className="w-full max-w-xl">
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search JO, tracking no, client, payee, RFP"
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue focus:ring-2 focus:ring-blue/10"
            />
          </div>
        </div>
      </header>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {[
          { label: "Total JO", value: summary.total, tone: "blue" },
          { label: "Active", value: summary.active, tone: "emerald" },
          { label: "Nomination", value: summary.nomination, tone: "amber" },
          { label: "With Request", value: summary.withRequest, tone: "emerald" },
          { label: "No Request", value: summary.withoutRequest, tone: "rose" },
        ].map((card) => (
          <div
            key={card.label}
            className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              {card.label}
            </div>
            <div className="mt-3 text-3xl font-semibold text-slate-900">{card.value}</div>
          </div>
        ))}
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {[
              { key: "all", label: "All Stages" },
              { key: "active", label: "Active" },
              { key: "nomination", label: "Nomination" },
            ].map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setStageFilter(item.key)}
                className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${
                  stageFilter === item.key
                    ? "bg-blue text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {[
              { key: "all", label: "All JO" },
              { key: "with-request", label: "With Request" },
              { key: "without-request", label: "No Request" },
            ].map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setCoverageFilter(item.key)}
                className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${
                  coverageFilter === item.key
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 text-sm text-slate-500">
          Showing {filteredRows.length} job order{filteredRows.length === 1 ? "" : "s"}.
        </div>

        {loading ? (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-16 text-center text-sm text-slate-500">
            Loading JO request coverage...
          </div>
        ) : error ? (
          <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-5 text-sm text-rose-700">
            {error}
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-slate-200 px-4 py-16 text-center text-sm text-slate-500">
            No job orders matched the current filters.
          </div>
        ) : (
          <>
            <div className="mt-6 overflow-x-auto">
              <table className="min-w-[1550px] divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="min-w-[180px] px-4 py-3 text-left font-semibold text-slate-600">Job Order</th>
                    <th className="min-w-[110px] px-4 py-3 text-left font-semibold text-slate-600">Stage</th>
                    <th className="min-w-[150px] px-4 py-3 text-left font-semibold text-slate-600">Tracking No.</th>
                    <th className="min-w-[220px] px-4 py-3 text-left font-semibold text-slate-600">Client</th>
                    <th className="min-w-[170px] px-4 py-3 text-left font-semibold text-slate-600">Shipment Status</th>
                    <th className="min-w-[200px] px-4 py-3 text-left font-semibold text-slate-600">Assigned</th>
                    <th className="min-w-[180px] px-4 py-3 text-left font-semibold text-slate-600">Request Coverage</th>
                    <th className="min-w-[320px] px-4 py-3 text-left font-semibold text-slate-600">Requests</th>
                    <th className="min-w-[140px] px-4 py-3 text-left font-semibold text-slate-600">Request Total</th>
                    <th className="min-w-[140px] px-4 py-3 text-left font-semibold text-slate-600">Shipment Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {paginatedRows.map((row) => (
                    <tr key={row.id} className="align-top">
                      <td className="min-w-[180px] px-4 py-4">
                        <div className="font-semibold text-blue">{row.job_order}</div>
                        <div className="mt-1 text-xs text-slate-500">
                          {row.shipment_type} • {row.shipment_mode}
                        </div>
                      </td>
                      <td className="min-w-[110px] px-4 py-4">
                        <span className="rounded-full bg-blue/10 px-2.5 py-1 text-xs font-semibold text-blue">
                          {row.stage}
                        </span>
                      </td>
                      <td className="min-w-[150px] px-4 py-4 text-slate-700">{row.tracking_no}</td>
                      <td className="min-w-[220px] px-4 py-4 text-slate-700">{row.corporate_client}</td>
                      <td className="min-w-[170px] px-4 py-4">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${getStatusToneClass(row.shipment_status)}`}>
                          {row.shipment_status}
                        </span>
                      </td>
                      <td className="min-w-[200px] px-4 py-4 text-slate-700">{row.assigned_to}</td>
                      <td className="min-w-[180px] px-4 py-4">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${getRequestCoverageClass(row.has_request)}`}>
                          {row.has_request ? `${row.request_count} request${row.request_count === 1 ? "" : "s"}` : "No request submitted"}
                        </span>
                      </td>
                      <td className="min-w-[320px] px-4 py-4">
                        {row.has_request ? (
                          <div className="space-y-3">
                            {row.linked_requests.map((request) => (
                              <div
                                key={request.id || `${request.rfp_no}-${request.payee}`}
                                className="rounded-2xl border border-slate-100 bg-slate-50/80 px-3 py-2"
                              >
                                <div className="font-semibold text-slate-900">{request.rfp_no}</div>
                                <div className="mt-1 text-xs text-slate-500">{request.payee}</div>
                                <div className="mt-2">
                                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${getStatusToneClass(request.status)}`}>
                                    {request.status}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="min-w-[140px] px-4 py-4 text-slate-700">
                        {row.has_request ? `₱${formatCurrency(row.total_request_amount)}` : "—"}
                      </td>
                      <td className="min-w-[140px] px-4 py-4 text-slate-700">{formatDate(row.shipment_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-slate-500">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  First
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((page) => Math.min(totalPages, page + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Last
                </button>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default JoRequests;
